import _ from 'lodash';
import config from './config';
import type { Data } from '../types/api';
import { message } from 'antd';
import { getCurrentLanguage } from '../i18n/languages';
import { resolveRequestRegion } from './region';
export type JSONArray = Array<JSONValue>;
export interface JSONObject {
  [x: string]: JSONValue;
}
export type JSONValue = string | number | boolean | JSONObject | JSONArray | undefined | Blob | null;

export type RequestProps = {
  uri?: string;
  path: string;
  params?: JSONObject;
  headers?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
};

let Authorization: string | null = null;
let adminAuthorization: string | null = null;
let storeid: string | null = null;

export function setToken(t: string | null) {
  Authorization = t ? `Bearer ${t}` : null;
}

export function setAdminToken(accessToken: string | null) {
  adminAuthorization = accessToken ? `Bearer ${accessToken}` : null;
}

export function setStoreid(sid: string | null) {
  storeid = sid;
}

function _buildParams(data: JSONObject): Array<[string, any]> {
  let params: Array<any> = [];
  _.forEach(data, (value: any, key: string) => {
    if (typeof value === 'undefined' || value === null) {
      //
    } else if (Array.isArray(value)) {
      _.forEach(value, (v: Object) => params.push([key, v]));
    } else {
      params.push([key, value]);
    }
  });
  for (let pair of params) {
    if (pair.length > 1 && pair[1] instanceof Date) {
      pair[1] = pair[1].getTime() / 1000;
    }
  }
  return params;
}
function _buildFormData(data: JSONObject): FormData {
  let formData = new FormData();
  _buildParams(data).forEach((value: Array<any>) => formData.append(value[0], value[1]));
  return formData;
}
function _buildRawData(data: JSONObject): string {
  return Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key] as string | number | boolean)}`)
    .join('&');
}
export function _buildQueryUrl(url: string, data: JSONObject): string {
  const params = _buildParams(data)
    .map((value: Array<any>) => `${encodeURIComponent(value[0])}=${encodeURIComponent(value[1])}`)
    .join('&');
  if (params.length === 0) {
    return url;
  }
  return `${url}?${params}`;
}

function _buildHeaders(path?: string): Record<string, string> {
  const headers: Record<string, string> = {
    language: getCurrentLanguage(),
    region: resolveRequestRegion()
  };
  const adminRequest =
    (typeof window !== 'undefined' && window.location.pathname.startsWith('/devops/creator-outreach')) ||
    Boolean(path?.includes('/console/'));
  const authorization = adminRequest ? adminAuthorization : Authorization;
  if (authorization) {
    headers.Authorization = authorization;
  }
  if (storeid) {
    headers.Storeid = storeid;
  }
  return headers;
}

function _mergeHeaders(
  extra?: Record<string, string | number | undefined>,
  path?: string
): Record<string, string> {
  const headers = _buildHeaders(path);
  if (!extra) return headers;
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined) headers[key] = String(value);
  });
  return headers;
}

async function _readBody(response: Response): Promise<any> {
  const text = await response.text();
  const trimmed = text.trim();
  if (!trimmed) return null;
  const contentType = response.headers.get('Content-Type') ?? '';
  if (
    contentType.includes('application/json') ||
    trimmed.startsWith('{') ||
    trimmed.startsWith('[')
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return text;
    }
  }
  return text;
}

const AUTH_ENDPOINT_RE =
  /\/api\/v1\/auth\/(login|login\/code|register|verification-code|wechat\/qrcode|console\/login)(?:\?|$)/;

let handlingUnauthorized = false;

function isAdminContext(requestUri: string) {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/devops/creator-outreach')) {
    return true;
  }
  return requestUri.includes('/console/') || requestUri.includes('/api/v1/auth/console/');
}

/** 登录过期：清会话并跳登录页。登录类接口本身的 401 不处理，避免循环。 */
function handleUnauthorized(requestUri: string) {
  if (typeof window === 'undefined') return;
  if (AUTH_ENDPOINT_RE.test(requestUri)) return;
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;

  const admin = isAdminContext(requestUri);
  if (admin) {
    localStorage.removeItem('admin_user');
    setAdminToken(null);
    const loginPath = '/devops/creator-outreach/login';
    if (window.location.pathname.startsWith(loginPath)) {
      handlingUnauthorized = false;
      return;
    }
    message.warning('登录已过期，请重新登录');
    window.location.assign(loginPath);
    return;
  }

  localStorage.removeItem('user');
  localStorage.removeItem('expire');
  setToken(null);
  const loginPath = '/creator/login';
  if (window.location.pathname.startsWith(loginPath)) {
    handlingUnauthorized = false;
    return;
  }
  message.warning('登录已过期，请重新登录');
  window.location.assign(loginPath);
}

async function _patchFetchResponse<T>(
  uri: string,
  params: Record<string, FormData | JSONObject | string | AbortSignal | undefined>
): Promise<Data<T>> {
  let response: Response;

  try {
    response = await fetch(uri, params);

    const data = await _readBody(response);
    const businessCode =
      data && typeof data === 'object' && typeof (data as { code?: unknown }).code === 'number'
        ? (data as { code: number }).code
        : undefined;
    if (response.status === 401 || businessCode === 401) {
      handleUnauthorized(uri);
    }
    if (data && typeof data === 'object') {
      return {
        ...data,
        code: typeof businessCode === 'number' ? businessCode : response.status,
        message: typeof (data as { message?: unknown }).message === 'string'
          ? (data as { message: string }).message
          : ''
      };
    }
    if (!response.ok) {
      return {
        code: response.status,
        message: typeof data === 'string' ? data : '',
        data: null as unknown as T
      };
    }
    return { code: response.status, message: '', data: data as T };
  } catch (e: unknown) {
    console.log(e);
    message.error('接口调用错误');
    return {
      code: -1,
      message: '接口调用错误',
      data: null as unknown as T
    };
  }
}

export function get<T>({ uri = config.baseUrl, path, params, signal }: RequestProps) {
  if (config.LOG_ON) {
    console.log('get:', uri + path, params);
  }
  return _patchFetchResponse<T>(_buildQueryUrl(uri + path, params || {}), {
    method: 'GET',
    headers: _mergeHeaders(undefined, path),
    signal
  });
}

export function post<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'POST',
    body: params && _buildFormData(params),
    headers: _mergeHeaders(headers, path)
  });
}

export function postArray<T>({
  uri = config.baseUrl,
  path,
  params,
  headers
}: {
  uri?: string;
  path: string;
  params: T[];
  headers?: Record<string, string>;
}): Promise<Data<T>> {
  if (config.LOG_ON) {
    console.log('postArray:', uri + path, params);
  }
  return _patchFetchResponse<T>(uri + path, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: _mergeHeaders(
      {
        'Content-Type': 'application/json',
        ...headers
      },
      path
    )
  });
}

export function postJSON<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  if (config.LOG_ON) {
    console.log('postJSON:', uri + path, params);
  }
  return _patchFetchResponse<T>(uri + path, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: _mergeHeaders({ 'Content-Type': 'application/json', ...headers }, path)
  });
}

export function postRaw<T>({ uri = config.baseUrl, path, params }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'POST',
    body: params && _buildRawData(params),
    headers: _mergeHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }, path)
  });
}
export function deleteRequest<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'DELETE',
    body: params && _buildFormData(params),
    headers: _mergeHeaders(headers, path)
  });
}

export function deleteJSON<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'DELETE',
    body: JSON.stringify(params),
    headers: _mergeHeaders({ 'Content-Type': 'application/json', ...headers }, path)
  });
}

export function put<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'PUT',
    body: params && _buildFormData(params),
    headers: _mergeHeaders(headers, path)
  });
}

export function putJSON<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'PUT',
    body: JSON.stringify(params),
    headers: _mergeHeaders({ 'Content-Type': 'application/json', ...headers }, path)
  });
}

export function patchJSON<T>({ uri = config.baseUrl, path, params, headers }: RequestProps) {
  return _patchFetchResponse<T>(uri + path, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: _mergeHeaders({ 'Content-Type': 'application/json', ...headers }, path)
  });
}

export async function downloadFile(path: string, method: string, params?: JSONObject) {
  const request = {
    body: JSON.stringify(params),
    method,
    headers: _mergeHeaders({ 'Content-Type': 'application/json;charset=UTF-8' }, path)
  };
  const uri = `${config.baseUrl}${path}`;
  const response = await fetch(uri, request);
  if (response.status === 401) {
    handleUnauthorized(uri);
    return;
  }

  if (response.ok) {
    const filename = response.headers.get('content-disposition')?.split(';')[1].split('=')[1];
    const blob = await response.blob();

    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    if (filename) {
      link.download = decodeURIComponent(filename);
    }
    link.style.display = 'none';
    link.href = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
